import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1758957701419-2c6e266f7988?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxjYWxtJTIwcmVzaWRlbnRpYWwlMjBob21lJTIwaW50ZXJpb3IlMjBtb2Rlcm58ZW58MHx8fHwxNzg2NDYyMjIwfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={BG} alt="Residential home" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0F172A]/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-white/15 flex items-center justify-center"><Wallet className="h-6 w-6" /></div>
            <div className="leading-tight">
              <div className="font-head font-bold text-xl tracking-[0.2em]">AYOBELL</div>
              <div className="font-head font-medium text-sm text-slate-300">HouseSpend</div>
            </div>
          </div>
          <div>
            <h1 className="font-head text-4xl font-semibold leading-tight max-w-md">Every home. Every receipt. Real-time visibility.</h1>
            <p className="text-slate-300 mt-4 max-w-md">Secure petty-cash and weekly budget management for residential children&apos;s homes.</p>
          </div>
          <div className="text-xs text-slate-400">Role-based access · Full audit trail · Secure receipt storage</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-lg bg-[#0F172A] flex items-center justify-center"><Wallet className="h-6 w-6 text-white" /></div>
              <div className="leading-tight">
                <div className="font-head font-bold text-xl tracking-[0.2em] text-slate-900" data-testid="login-master-brand">AYOBELL</div>
                <div className="font-head font-medium text-sm text-slate-500" data-testid="login-product-name">HouseSpend</div>
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-3" data-testid="login-tagline">Smart spending. Complete visibility.</p>
          </div>
          <h2 className="font-head text-2xl font-semibold text-slate-900">Sign in</h2>
          <p className="text-slate-500 text-sm mt-1 mb-6">Enter your credentials to continue</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@organisation.com" className="mt-1.5" required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" required />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-[#0F172A] hover:bg-[#1E293B] h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <div className="mt-10 text-center text-xs text-slate-400" data-testid="login-powered-by">
            Powered by <span className="font-semibold text-slate-600 tracking-wide">AYOBELL</span>
            <div className="mt-0.5">TechSpace Solutions Ltd</div>
          </div>
        </div>
      </div>
    </div>
  );
}
